import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, concatMap, EMPTY, filter, first, map, retry, Subject, Subscription, switchMap, takeUntil, tap, timer } from 'rxjs';
import { FeatureToggleCardComponent } from '../../common/feature-toggle-card/feature-toggle-card.component';
import { ProgressBarComponent } from '../../common/progress-bar/progress-bar.component';
import { ProcessLogsLoaderComponent } from '../../common/process-logs-loader/process-logs-loader.component';
import { ProcessLogEntry } from '../../common/process-logs-loader/process-log-entry';
import { RestService } from '../../../core/services/rest.service';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';

export type TcgGenerateSource = 'user_stories' | 'other_documents';
export type TcgTestType = 'smoke' | 'sanity' | 'regression';
export type ExtractTab = 'alm' | 'requirement_doc' | 'other_sources';

export interface SavedDocument {
  type: string;
  name: string;
  file: File;
  connectionId?: string;
}

export interface TcgGenerateFormData {
  source: TcgGenerateSource;
  // Step 2 – document selection (other_documents only)
  documentType: string;
  documentName: string;
  savedDocuments: SavedDocument[];
  /** File connection id used for the BPD / other-documents pipeline. */
  connectionId?: string;
  // Step 3 – test case type
  functionalTestCases: boolean;
  testType: TcgTestType;
  nonFunctionalTestCases: boolean;
  // Pipeline result – raw response from GET tcg/file/bpd/testcases
  testCases?: any;
  // ALM / storage extraction result
  extractedStories?: any[];
  extractConfigId?: string;
  /** When true, parent polls progress and streams stories into the list. */
  pollExtraction?: boolean;
  /** ALM "Extract From" date selected in the popup — forwarded to progress polls. */
  fromTimestamp?: string;
  processingCounts?: {
    identified?: number;
    newCount?: number;
    extractionCompleted?: number;
    extractionPending?: number;
    rciGenerated?: number;
    kgGenerated?: number;
  };
}

/**
 * Steps:
 *  1 → source selection (both flows)
 *  2 → document selection (other_documents only; user_stories skips to 3)
 *  3 → test case type selection (both flows)
 *  4 → progress / generation
 */
type ModalStep = 1 | 2 | 3 | 4;

@Component({
  selector: 'app-tcg-generate-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, FeatureToggleCardComponent, ProgressBarComponent, ProcessLogsLoaderComponent, NzDatePickerModule],
  templateUrl: './tcg-generate-modal.component.html',
  styleUrl: './tcg-generate-modal.component.scss',
})
export class TcgGenerateModalComponent implements OnInit, OnDestroy {
  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);

  @Input() projectId = '';

  // ── Step tracking ─────────────────────────────────────────────────────────
  currentStep: ModalStep = 1;

  // ── Step 1 ───────────────────────────────────────────────────────────────
  selectedSource: TcgGenerateSource = 'user_stories';

  // ── Step 2 – Extract User Stories (user_stories flow) ────────────────────
  extractTab: ExtractTab = 'alm';

  // ALM Connection tab
  almProvider = '';
  almConnectionName = '';
  almExtractFrom: Date | null = null;
  disableFutureDates = (d: Date) => d > new Date();
  almProviderOptions: string[] = [];

  // Requirement Document tab (user_stories flow — separate from other_documents state)
  usReqDocType = '';
  usReqDocName = '';
  usIsAddingNewDoc = false;
  usNewDocName = '';
  usNewDocFile: File | null = null;
  /** Preserved across onUsConfirmDocument so the file is available when Extract is clicked. */
  private usNewDocSavedFile: File | null = null;

  // Other Sources tab
  otherConnType = '';
  otherConnName = '';
  otherConnTypeOptions: string[] = ['S3'];
  otherConnNameOptions: string[] = [];
  otherIsAddingNewConn = false;
  otherNewConnFile: File | null = null;
  /** Preserved across confirm so the file is available when Extract is clicked (fallback if upload deferred). */
  private otherNewConnSavedFile: File | null = null;
  isUploadingOtherConn = false;
  otherUploadError = '';
  readonly otherSourceAcceptFormats = '.xlsx,.xls';
  readonly otherSourceUploadHint = 'Excel file (.xlsx, .xls)';

  // Application Context (shared across all extract tabs) — same BE flow as app-user-stories
  addAppContext = false;
  /** Selected existing FILE connection ID (pdf/docx) for "use existing". */
  appContextValue = '';
  /** Available PDF/DOCX FILE connections usable as application context. */
  appContextOptions: { id: string; name: string }[] = [];
  newAppContextFile: File | null = null;
  /** Project-level application context returned by GET /tcg/applicationContext/{projectId}. */
  uploadedApplicationContext: any = null;
  isAppContextProgress = false;
  isAppContextProgressError = false;
  appContextProgressText = '';
  appContextProgressPct = 3;
  appContextError = '';
  isDeletingAppContext = false;
  readonly appContextAcceptFormats = '.pdf,.docx';
  readonly appContextUploadHint = 'Only .PDF & .DOCX';

  // ── Step 2 – document selection (other_documents) ────────────────────────
  documentType = '';
  documentName = '';
  isAddingNewDocument = false;
  newDocumentName = '';
  newDocumentFile: File | null = null;
  documentTypeTouched = false;
  isLoadingConnections = false;
  isUploadingDocument = false;
  uploadError = '';
  savedDocuments: SavedDocument[] = [];

  readonly documentTypeOptions: { value: string; disabled?: boolean }[] = [
    { value: 'FSD/BRD' },
    { value: 'UI/UX', disabled: true },
    { value: 'API DOCS', disabled: true },
  ];

  /** Document types shown when browsing existing files in Requirement Document tab. */
  readonly usReqDocTypeOptions: string[] = ['PDF', 'XLSX', 'DOCX'];

  /** Accepted formats for user-stories → Requirement Document upload. */
  readonly usReqDocAcceptFormats = '.pdf,.docx,.xls,.xlsx';
  readonly usReqDocUploadHint = 'PDF, Excel, or Word document (.pdf, .xlsx, .docx)';

  /** Template download items for the Requirement Document tab. */
  readonly reqDocSampleFiles = [
    { icon: 'assets/images/pdf.svg',   name: 'User Story Template.pdf',  type: 'PDF',  loading: false },
    { icon: 'assets/images/docx.svg',  name: 'User Story Template.docx', type: 'DOCX', loading: false },
    { icon: 'assets/images/excel.svg', name: 'User Story Template.xlsx', type: 'XLSX', loading: false },
  ];

  /** File extensions accepted per document type. */
  readonly docTypeAcceptMap: Record<string, string> = {
    'FSD/BRD': '.pdf,.docx',
    'UI/UX': '.fig,.png,.jpg,.jpeg,.svg,.webp',
    'API DOCS': '.json',
  };

  /** Human-readable format hint per document type. */
  readonly docTypeHintMap: Record<string, string> = {
    'FSD/BRD': 'PDF or Word document (.pdf, .docx)',
    'UI/UX': 'Figma or image file (.fig, .png, .jpg, .svg)',
    'API DOCS': 'JSON file (.json)',
  };

  getAcceptedFormats(docType: string): string {
    return this.docTypeAcceptMap[docType] ?? '.pdf,.docx,.json,.fig,.png,.jpg,.jpeg,.svg,.webp';
  }

  getFormatHint(docType: string): string {
    return this.docTypeHintMap[docType] ?? 'Select a document type first';
  }

  /** Returns file extensions for a Requirement Document type (PDF / XLSX / DOCX). */
  private extensionsForUsReqDocType(docType: string): string[] {
    const map: Record<string, string[]> = {
      PDF: ['pdf'],
      XLSX: ['xlsx', 'xls'],
      DOCX: ['docx', 'doc'],
    };
    return map[docType] ?? [];
  }

  /** Returns the lowercase file extensions (without leading dot) accepted for a given document type label. */
  private extensionsForDocType(docType: string): string[] {
    return this.getAcceptedFormats(docType)
      .split(',')
      .map(e => e.trim().replace(/^\./, '').toLowerCase());
  }

  /** All extensions allowed in the Requirement Document browse flow. */
  private get usReqDocAllowedExtensions(): string[] {
    return ['pdf', 'xlsx', 'xls', 'docx', 'doc'];
  }
  /** All FILE connections fetched from BE, including ID for API calls. */
  private allConnections: { id: string; name: string; extension: string }[] = [];
  /** Other Sources connections (S3 / XLSX) with IDs, used for extraction. */
  private allOtherConnections: { id: string; name: string; kind: 'S3' | 'XLSX' }[] = [];
  /** ALM connections with IDs, used for ALM extraction. */
  private allAlmConnections: { id: string; name: string; provider: string }[] = [];
  /** Manually added document names (from add-new flow), available for all types. */
  private manualDocumentNames: { name: string; type: string }[] = [];

  /** Connection ID of the document selected in the other_documents Step 2.
   *  Prefers the ID of a newly uploaded saved document, then falls back to
   *  the ID from the existing FILE connections list. */
  get selectedConnectionId(): string {
    // Check saved (newly uploaded) documents first
    const savedConn = this.savedDocuments.find(d => d.name === this.documentName)?.connectionId;
    if (savedConn) return savedConn;
    // Fall back to existing connection from BE
    return this.allConnections.find(c => c.name === this.documentName)?.id ?? '';
  }

  /** Names filtered by the currently selected documentType (browse mode). */
  get documentNameOptions(): string[] {
    if (!this.documentType) {
      return this.allConnections.map(c => c.name);
    }
    const exts = this.extensionsForDocType(this.documentType);
    const fromBE = this.allConnections
      .filter(c => exts.includes(c.extension.toLowerCase()))
      .map(c => c.name);
    const fromManual = this.manualDocumentNames
      .filter(d => d.type === this.documentType)
      .map(d => d.name);
    return [...new Set([...fromBE, ...fromManual])];
  }

  /** Names filtered by documentType selected inside the add-new card (new doc flow). */
  get newDocumentNameOptions(): string[] {
    if (!this.documentType) {
      return this.allConnections.map(c => c.name);
    }
    const exts = this.extensionsForDocType(this.documentType);
    return this.allConnections
      .filter(c => exts.includes(c.extension.toLowerCase()))
      .map(c => c.name);
  }

  // ── Step 3 – test case type ───────────────────────────────────────────────
  functionalTestCases = false;
  testType: TcgTestType = 'smoke';
  nonFunctionalTestCases = false;

  readonly testTypeOptions: { value: TcgTestType; label: string }[] = [
    { value: 'smoke', label: 'Smoke Testing' },
    { value: 'sanity', label: 'Sanity Testing' },
    { value: 'regression', label: 'Regression Testing' },
  ];

  // ── Step 4 – progress (BPD / other_documents only) ─────────────────────────
  progressValue = 0;
  progressText = 'Generating test cases...';
  progressPhase: 'uploading' | 'kg' | 'generating' | 'done' = 'uploading';
  isProgressError = false;
  /** Timeline entries for app-process-logs-loader. */
  processLogs: ProcessLogEntry[] = [];
  configId = '';
  /** True while user-stories extract APIs are starting (no progress step). */
  isStartingExtract = false;
  extractStartError = '';
  private _progressTimer: any = null;
  private _tickTimer: any = null;
  private _pendingResult: TcgGenerateFormData | null = null;
  private readonly _destroy$ = new Subject<void>();
  private _pollSub: Subscription | null = null;

  get processStatusLabel(): string {
    if (this.isProgressError) return 'Failed';
    if (this.progressPhase === 'done') return 'Completed';
    return 'Thinking...';
  }

  get isProcessActive(): boolean {
    return !this.isProgressError && this.progressPhase !== 'done';
  }

  // ── Computed helpers ──────────────────────────────────────────────────────

  get isOtherDocuments(): boolean {
    return this.selectedSource === 'other_documents';
  }

  get totalSteps(): number {
    return this.isOtherDocuments ? 3 : 2;
  }

  get displayStep(): number {
    // Clamp to totalSteps so progress step (4) shows the final step number
    return Math.min(this.currentStep, this.totalSteps);
  }

  get stepLabel(): string {
    return `Step ${this.displayStep} of ${this.totalSteps}`;
  }

  // ── Extract User Stories validity ────────────────────────────────────────

  /** ALM connection names filtered to only those matching the selected provider. */
  get almConnectionOptions(): string[] {
    if (!this.almProvider) {
      return this.allAlmConnections.map(c => c.name);
    }
    return this.allAlmConnections
      .filter(c => c.provider === this.almProvider)
      .map(c => c.name);
  }

  get isExtractStepValid(): boolean {
    if (this.extractTab === 'alm') {
      return !!this.almProvider && !!this.almConnectionName;
    }
    if (this.extractTab === 'requirement_doc') {
      return !!this.usReqDocType && !!this.usReqDocName;
    }
    return !!this.otherConnType && !!this.otherConnName;
  }

  get usReqDocNameOptions(): string[] {
    const allowedExts = this.usReqDocType
      ? this.extensionsForUsReqDocType(this.usReqDocType)
      : this.usReqDocAllowedExtensions;

    const fromBE = this.allConnections
      .filter(c => allowedExts.includes(c.extension.toLowerCase()))
      .map(c => c.name);
    const fromManual = this.manualDocumentNames
      .filter(d => !this.usReqDocType || d.type === this.usReqDocType)
      .map(d => d.name);
    return [...new Set([...fromBE, ...fromManual])];
  }

  get isOtherNewConnFormValid(): boolean {
    return !!this.otherNewConnFile;
  }

  get isUsNewDocFormValid(): boolean {
    return !!this.usNewDocFile;
  }

  /** Maps the saved requirement-doc type to the correct image asset name. */
  get usReqDocIconName(): string {
    if (this.usReqDocType === 'PDF') return 'pdf';
    if (this.usReqDocType === 'XLSX') return 'excel';
    return 'docx';
  }

  get canUploadAppContext(): boolean {
    return !!this.newAppContextFile && !this.isAppContextProgress && !!this.projectId;
  }

  get canApplyExistingAppContext(): boolean {
    return !!this.appContextValue && !this.isAppContextProgress && !!this.projectId;
  }

  get uploadedAppContextFileName(): string {
    const raw = this.uploadedApplicationContext?.originalFileName ?? '';
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  get uploadedAppContextIcon(): string {
    const ext = this.uploadedAppContextFileName.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return 'assets/images/pdf.svg';
    if (ext === 'docx' || ext === 'doc') return 'assets/images/docx.svg';
    return 'assets/images/docx.svg';
  }

  // ── Other Documents (Step 2) validity ────────────────────────────────────

  get isNewDocumentFormValid(): boolean {
    return !!this.documentType && !!this.newDocumentFile;
  }

  get isStep2Valid(): boolean {
    if (this.isAddingNewDocument) {
      return this.isNewDocumentFormValid;
    }
    return this.savedDocuments.length > 0 || (!!this.documentType && !!this.documentName);
  }

  get isStep3Valid(): boolean {
    return this.functionalTestCases || this.nonFunctionalTestCases;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.projectId) {
      this.loadConnections();
    }
  }

  ngOnDestroy(): void {
    if (this._progressTimer) {
      clearInterval(this._progressTimer);
    }
    this.stopProgressTick();
    this._destroy$.next();
    this._destroy$.complete();
    this._pollSub?.unsubscribe();
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  onContinue(): void {
    if (this.currentStep === 1) {
      // Both flows go to Step 2:
      // - user_stories → Extract User Stories
      // - other_documents → Document selection
      this.currentStep = 2;
    } else if (this.currentStep === 2) {
      // Only other_documents reaches Step 3 (test case type)
      this.currentStep = 3;
    }
  }

  onBack(): void {
    if (this.isAppContextProgress) {
      return;
    }
    if (this.currentStep === 2) {
      this.currentStep = 1;
    } else if (this.currentStep === 3) {
      this.currentStep = 2;
    }
  }

  onCancel(): void {
    if (this.isAppContextProgress) {
      return;
    }
    this.activeModal.dismiss();
  }

  /** Extract button for user_stories flow — starts API then closes (no progress bar). */
  onExtract(): void {
    if (!this.isExtractStepValid || this.isStartingExtract) {
      return;
    }
    this.extractStartError = '';
    this._pendingResult = {
      source: this.selectedSource,
      documentType: this.usReqDocType || this.documentType,
      documentName: this.usReqDocName || this.documentName,
      savedDocuments: this.savedDocuments,
      functionalTestCases: this.functionalTestCases,
      testType: this.testType,
      nonFunctionalTestCases: this.nonFunctionalTestCases,
    };

    if (this.extractTab === 'alm') {
      this.startAlmPipeline();
    } else if (this.extractTab === 'requirement_doc') {
      this.startReqDocPipeline();
    } else if (this.extractTab === 'other_sources') {
      this.startOtherSourcePipeline();
    } else {
      this.startProgress();
    }
  }

  onGenerate(): void {
    if (!this.isStep3Valid) {
      return;
    }
    this._pendingResult = {
      source: this.selectedSource,
      documentType: this.documentType,
      documentName: this.documentName,
      savedDocuments: this.savedDocuments,
      connectionId: this.selectedConnectionId || undefined,
      functionalTestCases: this.functionalTestCases,
      testType: this.testType,
      nonFunctionalTestCases: this.nonFunctionalTestCases,
    };

    const connId = this.selectedConnectionId;

    if (this.isOtherDocuments && connId && this.projectId) {
      // Full BPD pipeline: upload → generate KG → poll progress
      this.startBpdPipeline(connId);
    } else {
      // Fallback: simulate progress (user_stories or no connection ID resolved)
      this.startProgress();
    }
  }

  // ── Step 1 ───────────────────────────────────────────────────────────────

  selectSource(source: TcgGenerateSource): void {
    this.selectedSource = source;
  }

  // ── Step 2 – Extract User Stories tab/field actions ──────────────────────

  switchExtractTab(tab: ExtractTab): void {
    this.extractTab = tab;
    this.otherIsAddingNewConn = false;
  }

  onAlmProviderChange(): void {
    // Reset and auto-select the first connection for the new provider
    const filtered = this.almConnectionOptions; // getter already filters by almProvider
    this.almConnectionName = filtered.length ? filtered[0] : '';
  }

  onUsReqDocTypeChange(): void {
    this.usReqDocName = '';
    this.usNewDocSavedFile = null;
  }

  onUsAddNewDocument(): void {
    this.usIsAddingNewDoc = true;
    this.usNewDocName = '';
    this.usNewDocFile = null;
  }

  onOtherAddNewConnection(): void {
    this.otherIsAddingNewConn = true;
    this.otherNewConnFile = null;
    this.otherUploadError = '';
    this.isUploadingOtherConn = false;
  }

  /** When Connection Type changes, refresh Connection Name options for that type. */
  onOtherConnTypeChange(): void {
    this.otherConnName = '';
    this.refreshOtherConnNameOptions();
  }

  onOtherNewConnFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.otherUploadError = '';

    if (file) {
      const accepted = this.otherSourceAcceptFormats
        .split(',')
        .map((ext) => ext.trim().toLowerCase());
      const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!accepted.includes(fileExt)) {
        this.otherNewConnFile = null;
        input.value = '';
        return;
      }
    }

    this.otherNewConnFile = file;
  }

  /**
   * Upload the selected Excel file via POST /settings/file/{projectId}
   * when the user confirms with the ✓ mark, then select it in the dropdown.
   */
  onOtherConfirmConnection(): void {
    if (!this.isOtherNewConnFormValid || !this.otherNewConnFile || this.isUploadingOtherConn) return;
    if (!this.projectId) {
      this.otherUploadError = 'Missing project. Please reopen the modal and try again.';
      return;
    }

    const savedFile = this.otherNewConnFile;
    const savedName = savedFile.name;

    this.isUploadingOtherConn = true;
    this.otherUploadError = '';

    const formData = new FormData();
    formData.append('type', 'local');
    formData.append('file', savedFile, savedFile.name);

    this.restService.createFileConnection(this.projectId, formData).subscribe({
      next: (res: any) => {
        const connectionId: string =
          res?.data?.id ?? res?.data?.connectionId ?? res?.id ?? res?.connectionId ?? '';

        // Replace any prior placeholder for this name and keep the server id
        this.allOtherConnections = [
          ...this.allOtherConnections.filter(c => !(c.name === savedName && c.kind === 'XLSX')),
          { id: connectionId, name: savedName, kind: 'XLSX' },
        ];

        this.otherConnType = 'XLSX';
        this.otherConnName = savedName;
        this.refreshOtherConnNameOptions();

        // Already uploaded — Extract should use connection id, not re-upload
        this.otherNewConnSavedFile = null;
        this.otherIsAddingNewConn = false;
        this.otherNewConnFile = null;
        this.isUploadingOtherConn = false;
      },
      error: (err: any) => {
        console.error('[TCG] Other Sources file upload failed', err);
        this.otherUploadError =
          err?.error?.message || err?.message || 'Upload failed. Please try again.';
        this.isUploadingOtherConn = false;
      },
    });
  }

  onUsNewDocFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (file) {
      const accepted = this.usReqDocAcceptFormats
        .split(',')
        .map((ext) => ext.trim().toLowerCase());
      const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!accepted.includes(fileExt)) {
        this.usNewDocFile = null;
        input.value = '';
        return;
      }
    }

    this.usNewDocFile = file;
    // Auto-confirm immediately for the direct-upload layout
    if (this.usNewDocFile) {
      this.onUsConfirmDocument();
    }
  }

  /** Remove the selected requirement document and reset the tab state. */
  clearUsReqDoc(event: Event): void {
    event.stopPropagation();
    this.usReqDocType = '';
    this.usReqDocName = '';
    this.usNewDocSavedFile = null;
    this.usNewDocFile = null;
  }

  /** Download a sample user-story template (PDF / DOCX / XLSX). */
  downloadReqDocTemplate(type: string): void {
    const file = this.reqDocSampleFiles.find(f => f.type === type);
    if (!file || !this.projectId) return;
    file.loading = true;
    this.restService.getSampleUserStoryFileDownload(this.projectId, type).subscribe({
      next: (item: any) => {
        file.loading = false;
        window.open(item?.data?.signedUrl, '_blank');
      },
      error: () => {
        file.loading = false;
      },
    });
  }

  onUsConfirmDocument(): void {
    if (!this.isUsNewDocFormValid || !this.usNewDocFile) return;

    const savedFile = this.usNewDocFile;
    const savedName = savedFile.name;
    const savedType = this.inferUsReqDocTypeFromFile(savedFile);

    if (!this.manualDocumentNames.some(d => d.name === savedName && d.type === savedType)) {
      this.manualDocumentNames = [...this.manualDocumentNames, { name: savedName, type: savedType }];
    }

    this.usReqDocType = savedType;
    this.usNewDocSavedFile = savedFile;
    this.usReqDocName = savedName;
    this.usIsAddingNewDoc = false;
    this.usNewDocName = '';
    this.usNewDocFile = null;
  }

  private inferUsReqDocTypeFromFile(file: File): string {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return 'PDF';
    if (ext === 'xlsx' || ext === 'xls') return 'XLSX';
    if (ext === 'docx' || ext === 'doc') return 'DOCX';
    return 'PDF';
  }

  onAppContextToggle(enabled: boolean): void {
    this.addAppContext = enabled;
    if (enabled) return;

    this.appContextError = '';
    this.newAppContextFile = null;
    this.isAppContextProgressError = false;

    if (this.uploadedApplicationContext?.originalFileName) {
      this.deleteUploadedAppContext();
    }
  }

  onNewAppContextFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.appContextError = '';

    if (file) {
      const accepted = this.appContextAcceptFormats
        .split(',')
        .map((ext) => ext.trim().toLowerCase());
      const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!accepted.includes(fileExt)) {
        this.newAppContextFile = null;
        this.appContextError = `Invalid file type. ${this.appContextUploadHint}`;
        input.value = '';
        return;
      }
    }

    this.newAppContextFile = file;
  }

  clearNewAppContextFile(event?: Event): void {
    event?.stopPropagation();
    this.newAppContextFile = null;
    this.appContextError = '';
  }

  triggerAppContextFileInput(fileInput: HTMLInputElement): void {
    if (this.isAppContextProgress) return;
    fileInput.click();
  }

  /**
   * Same pipeline as app-user-stories.uploadAppContext:
   *   1) POST /settings/file/{projectId}  (FormData: file)
   *   2) POST /tcg/file/applicationContext
   *   3) Poll GET /tcg/applicationContext/{projectId} every 20s
   */
  uploadAppContext(): void {
    if (!this.canUploadAppContext || !this.newAppContextFile) return;

    const formData = new FormData();
    formData.append('file', this.newAppContextFile);

    this.beginAppContextProgress('Uploading application context...');

    this.restService.uploadAppContext(this.projectId, formData).pipe(
      switchMap((response: any) => {
        const connId: string = response?.data?.id ?? response?.data?.connectionId ?? '';
        if (!connId) {
          throw new Error('No connection ID returned from upload.');
        }
        this.appContextProgressText = 'Processing application context...';
        this.appContextProgressPct = Math.min(this.appContextProgressPct, 3);
        return this.restService.processApplicationContext(this.projectId, connId);
      }),
      switchMap(() => this.pollAppContextStatus()),
      takeUntil(this._destroy$),
      catchError((err: any) => {
        this.failAppContextProgress(
          err?.error?.message || err?.message || 'Upload failed. Please try again.'
        );
        return EMPTY;
      })
    ).subscribe();
  }

  /**
   * Process an already-uploaded FILE connection as application context
   * (skips the file upload step).
   */
  applyExistingAppContext(): void {
    if (!this.canApplyExistingAppContext) return;

    this.beginAppContextProgress('Processing application context...');

    this.restService.processApplicationContext(this.projectId, this.appContextValue).pipe(
      switchMap(() => this.pollAppContextStatus()),
      takeUntil(this._destroy$),
      catchError((err: any) => {
        this.failAppContextProgress(
          err?.error?.message || err?.message || 'Processing failed. Please try again.'
        );
        return EMPTY;
      })
    ).subscribe();
  }

  deleteUploadedAppContext(): void {
    if (!this.projectId || this.isDeletingAppContext || this.isAppContextProgress) return;

    this.isDeletingAppContext = true;
    this.appContextError = '';

    this.restService.deleteApplicationContext(this.projectId).subscribe({
      next: () => {
        this.uploadedApplicationContext = null;
        this.appContextValue = '';
        this.addAppContext = false;
        this.isDeletingAppContext = false;
      },
      error: (err: any) => {
        this.appContextError =
          err?.error?.message || err?.message || 'Failed to remove application context.';
        this.addAppContext = true;
        this.isDeletingAppContext = false;
      },
    });
  }

  /**
   * Loads the project's current application context (same as user-stories.getApplicationContext).
   */
  private loadExistingAppContext(): void {
    this.restService.handleAsyncApplicationContext(this.projectId).subscribe({
      next: (res: any) => {
        const data = res?.data;
        const originalFileName: string = data?.originalFileName ?? '';
        if (!originalFileName) return;

        this.uploadedApplicationContext = data;
        this.addAppContext = true;

        const match = this.appContextOptions.find(
          c => c.name === originalFileName || c.name.includes(originalFileName),
        );
        if (match) {
          this.appContextValue = match.id;
        }
      },
      error: () => {
        // No existing context is fine
      },
    });
  }

  private beginAppContextProgress(text: string): void {
    this.isAppContextProgress = true;
    this.isAppContextProgressError = false;
    this.appContextError = '';
    this.appContextProgressText = text;
    this.appContextProgressPct = 3;
  }

  /** Never let the visible % go backwards mid-run (chunk counts can start lower than the upload estimate). */
  private setAppContextProgressPct(next: number): void {
    const clamped = Math.max(0, Math.min(100, Math.round(Number(next) || 0)));
    if (clamped >= 100) {
      this.appContextProgressPct = 100;
      return;
    }
    this.appContextProgressPct = Math.max(this.appContextProgressPct, clamped);
  }

  private failAppContextProgress(message: string): void {
    this.isAppContextProgress = false;
    this.isAppContextProgressError = true;
    this.appContextError = message;
  }

  private pollAppContextStatus() {
    return timer(0, 20000).pipe(
      switchMap(() => this.restService.handleAsyncApplicationContext(this.projectId)),
      map((statusResponse: any) => {
        const data = statusResponse?.data ?? {};
        const status = (data.status ?? '').toUpperCase();
        this.appContextProgressText = data.message ?? 'Processing application context...';

        const totalChunks = Number(data.totalChunks) || 0;
        const processingChunks = Number(data.processingChunks) || 0;
        if (totalChunks > 0) {
          const chunkPct = Math.round((processingChunks / totalChunks) * 100);
          this.setAppContextProgressPct(Math.min(Math.max(chunkPct, 1), 99));
        } else {
          this.appContextProgressPct = 3;
        }

        if (status === 'COMPLETED') {
          this.appContextProgressPct = 100;
          this.appContextProgressText = data.message ?? 'Application context ready';
          this.uploadedApplicationContext = data;
          this.newAppContextFile = null;
          setTimeout(() => {
            this.isAppContextProgress = false;
          }, 1500);
          return true;
        }

        if (status === 'FAILED' || status === 'FAILURE') {
          throw new Error(data.message || 'Application context generation failed.');
        }

        return false;
      }),
      filter((done) => done === true),
      first(),
    );
  }

  // ── Step 2 – document actions (other_documents flow) ─────────────────────

  onAddNewDocument(): void {
    this.isAddingNewDocument = true;
    this.documentType = '';
    this.documentName = '';
    this.documentTypeTouched = false;
    this.newDocumentFile = null;
  }

  onDocumentTypeBlur(): void {
    this.documentTypeTouched = true;
  }

  onNewDocumentFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (file && this.documentType) {
      const accepted = this.getAcceptedFormats(this.documentType)
        .split(',')
        .map(ext => ext.trim().toLowerCase());
      const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!accepted.includes(fileExt)) {
        this.uploadError = `Invalid file type. Accepted: ${this.getFormatHint(this.documentType)}`;
        this.newDocumentFile = null;
        input.value = '';
        return;
      }
    }

    this.uploadError = '';
    this.newDocumentFile = file;
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  /** Upload document via API on ✓ click, then save to the list. */
  onConfirmDocument(): void {
    if (!this.isNewDocumentFormValid || this.isUploadingDocument) {
      return;
    }

    const savedType = this.documentType;
    const savedName = this.newDocumentFile!.name;
    const savedFile = this.newDocumentFile!;

    this.isUploadingDocument = true;
    this.uploadError = '';

    this.restService
      .uploadNewTcgDocument(this.projectId, savedFile, savedType, savedName)
      .subscribe({
        next: (res: any) => {
          // Extract the returned connection ID from the response
          const connectionId: string =
            res?.data?.id ?? res?.data?.connectionId ?? res?.id ?? res?.connectionId ?? '';

          // Add to saved list with the server-assigned connection ID
          this.savedDocuments = [
            ...this.savedDocuments,
            { type: savedType, name: savedName, file: savedFile, connectionId },
          ];

          // Register in filter map so it appears in dropdown
          if (!this.manualDocumentNames.some(d => d.name === savedName && d.type === savedType)) {
            this.manualDocumentNames = [
              ...this.manualDocumentNames,
              { name: savedName, type: savedType },
            ];
          }

          // Auto-select the browse-mode dropdowns
          this.documentType = savedType;
          this.documentName = savedName;

          // Collapse add-new card back to browse mode
          this.isAddingNewDocument = false;
          this.newDocumentFile = null;
          this.documentTypeTouched = false;
          this.isUploadingDocument = false;
        },
        error: (err: any) => {
          console.error('[TCG] Document upload failed', err);
          this.uploadError = err?.error?.message ?? 'Upload failed. Please try again.';
          this.isUploadingDocument = false;
        },
      });
  }

  /** Remove a saved document from the list. */
  onRemoveSavedDocument(index: number): void {
    this.savedDocuments = this.savedDocuments.filter((_, i) => i !== index);
  }

  // ── Step 3 – test type actions ────────────────────────────────────────────

  onFunctionalToggleChange(): void {
    if (this.functionalTestCases) {
      // Mutual exclusivity: turn off non-functional when functional is enabled
      this.nonFunctionalTestCases = false;
    } else {
      this.testType = 'smoke'; // reset to default on disable
    }
  }

  onNonFunctionalToggleChange(): void {
    if (this.nonFunctionalTestCases) {
      // Mutual exclusivity: turn off functional when non-functional is enabled
      this.functionalTestCases = false;
      this.testType = 'smoke';
    }
  }

  // ── Private ───────────────────────────────────────────────────────────────

  /** Called when Document Type dropdown changes in browse mode — reset the name selection. */
  onDocumentTypeChange(): void {
    this.documentName = '';
    this.newDocumentFile = null;
    this.uploadError = '';
  }

  /** Filter Connection Name options by the selected Other Sources connection type. */
  private refreshOtherConnNameOptions(): void {
    if (!this.otherConnType) {
      this.otherConnNameOptions = [];
      return;
    }
    this.otherConnNameOptions = this.allOtherConnections
      .filter(c => c.kind === this.otherConnType)
      .map(c => c.name);
  }

  private loadConnections(): void {
    this.isLoadingConnections = true;
    this.restService.getConnections(this.projectId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? {};

        // FILE connections → other_documents Step 2 + Application Context dropdown
        const fileConns: any[] = data.FILE ?? [];
        this.allConnections = fileConns
          .filter(c => !!c.connectionName)
          .map(c => ({
            id: (c.id ?? c.connectionId ?? c.sourceId ?? '') as string,
            name: c.connectionName as string,
            extension: (c.fileType ?? c.connectionName?.split('.').pop() ?? '').toString(),
          }));

        // Application Context options = PDF/DOCX FILE connections only (same accept types as user-stories)
        this.appContextOptions = this.allConnections
          .filter(c => ['pdf', 'docx', 'doc'].includes((c.extension || '').toLowerCase()))
          .map(c => ({ id: c.id, name: c.name }));

        // Load project-level application context if already configured
        this.loadExistingAppContext();

        // ALM connections → user_stories Extract, ALM tab
        // Provider comes from meta.source (e.g. JIRA, RALLY, AZURE_DEVOPS) — same as user-stories
        const almConns: any[] = data.ALM ?? [];
        this.allAlmConnections = almConns
          .filter(c => !!c.connectionName)
          .map(c => ({
            id: (c.id ?? c.connectionId ?? c.sourceId ?? '') as string,
            name: c.connectionName as string,
            provider: (c.meta?.source ?? c.connectionType ?? c.provider ?? '') as string,
          }));
        this.almProviderOptions = [
          ...new Set<string>(
            this.allAlmConnections
              .map(c => c.provider)
              .filter(Boolean)
          ),
        ];

        // Other Sources (S3 / XLSX) — same source as user-stories: FILE connections.
        // XLSX = excel files; S3 = S3-sourced FILE rows and/or dedicated S3/OTHER buckets.
        const s3BucketConns: any[] = [...(data.S3 ?? []), ...(data.OTHER ?? [])];
        const mapOtherConn = (c: any, kind: 'S3' | 'XLSX') => ({
          id: (c.id ?? c.connectionId ?? c.sourceId ?? '') as string,
          name: c.connectionName as string,
          kind,
        });
        const isExcelFile = (c: any): boolean => {
          const fileName = (c.meta?.fileName ?? c.connectionName ?? '').toString().toLowerCase();
          return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
        };
        const isS3File = (c: any): boolean => {
          const t = (c.meta?.type ?? c.meta?.source ?? '').toString().toLowerCase();
          return t === 's3' || t === 'aws_s3' || t === 'amazon_s3';
        };

        const xlsxFromFile = fileConns
          .filter(c => !!c.connectionName && isExcelFile(c))
          .map(c => mapOtherConn(c, 'XLSX'));
        const s3FromFile = fileConns
          .filter(c => !!c.connectionName && isS3File(c))
          .map(c => mapOtherConn(c, 'S3'));
        const s3FromBucket = s3BucketConns
          .filter(c => !!c.connectionName)
          .map(c => mapOtherConn(c, 'S3'));

        // Dedupe by id+kind so the same connection is not listed twice
        const seen = new Set<string>();
        this.allOtherConnections = [...xlsxFromFile, ...s3FromFile, ...s3FromBucket].filter(c => {
          const key = `${c.kind}:${c.id || c.name}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        this.refreshOtherConnNameOptions();

        this.isLoadingConnections = false;
      },
      error: () => {
        this.isLoadingConnections = false;
      },
    });
  }

  /**
   * Requirement Document extraction pipeline:
   *   Upload (if needed) → POST extractStories → close for parent polling.
   */
  private startReqDocPipeline(): void {
    this.isStartingExtract = true;
    this.extractStartError = '';

    if (this.usNewDocSavedFile) {
      const formData = new FormData();
      formData.append('type', 'local');
      formData.append('file', this.usNewDocSavedFile);

      this.restService.createFileConnection(this.projectId, formData).pipe(
        catchError((err: any) => {
          console.error('[TCG] File upload failed:', err);
          this.isStartingExtract = false;
          this.extractStartError = err?.error?.message || err?.message || 'File upload failed. Please try again.';
          return EMPTY;
        })
      ).subscribe({
        next: (response: any) => {
          const connId: string = response?.data?.id ?? '';
          this.triggerStorageExtraction(connId);
        },
      });
    } else {
      const connId = this.allConnections.find(c => c.name === this.usReqDocName)?.id ?? '';
      this.triggerStorageExtraction(connId);
    }
  }

  /**
   * Other Sources (S3/XLSX) extraction pipeline:
   *   POST extractStories → close for parent polling.
   */
  private startOtherSourcePipeline(): void {
    this.isStartingExtract = true;
    this.extractStartError = '';

    if (this.otherNewConnSavedFile && this.otherConnName === this.otherNewConnSavedFile.name) {
      const formData = new FormData();
      formData.append('type', 'local');
      formData.append('file', this.otherNewConnSavedFile);

      this.restService.createFileConnection(this.projectId, formData).pipe(
        catchError((err: any) => {
          console.error('[TCG] File upload failed:', err);
          this.isStartingExtract = false;
          this.extractStartError = err?.error?.message || err?.message || 'File upload failed. Please try again.';
          return EMPTY;
        })
      ).subscribe({
        next: (response: any) => {
          const connId: string = response?.data?.id ?? '';
          this.triggerStorageExtraction(connId);
        },
      });
      return;
    }

    const connId = this.allOtherConnections.find(c => c.name === this.otherConnName)?.id ?? '';
    this.triggerStorageExtraction(connId);
  }

  /**
   * Shared helper: POST tcg/file/extractStories → store configId → close modal.
   * Parent polls progress and streams stories into the list.
   */
  private triggerStorageExtraction(connectionId: string): void {
    this.restService.getStorageUserStories(this.projectId, connectionId).pipe(
      catchError((err: any) => {
        console.error('[TCG] Storage extraction failed:', err);
        this.isStartingExtract = false;
        this.extractStartError = err?.error?.message || err?.message || 'Extraction failed. Please try again.';
        return EMPTY;
      })
    ).subscribe({
      next: (res: any) => {
        this.configId = res?.data?.configId;
        this.closeForParentPolling();
      },
    });
  }

  /**
   * ALM extraction pipeline for "User Stories" flow:
   *   1. GET config — resume if IN_PROGRESS
   *   2. Otherwise POST extractStories
   *   3. Close immediately with configId (parent polls; no modal progress bar)
   */
  private startAlmPipeline(): void {
    const connectionId = this.allAlmConnections.find(c => c.name === this.almConnectionName)?.id ?? '';
    const dt = this.almExtractFrom ? new Date(this.almExtractFrom).toISOString() : undefined;

    this.isStartingExtract = true;
    this.extractStartError = '';

    this.restService.getAlmConfigByConnection(this.projectId, connectionId).pipe(
      catchError(() => {
        const payload = { projectId: this.projectId, almConnection: connectionId, fromTimestamp: dt };
        this.extractAlmStories(payload);
        return EMPTY;
      })
    ).subscribe((res: any) => {
      if (res?.data?.status === 'IN_PROGRESS') {
        this.configId = res.data.configId;
        this.closeForParentPolling();
      } else {
        const payload = { projectId: this.projectId, almConnection: connectionId, fromTimestamp: dt };
        this.extractAlmStories(payload);
      }
    });
  }

  private extractAlmStories(payload: any): void {
    this.restService.extractUserStoriesALM(payload).pipe(
      retry({
        count: 1,
        delay: (error, retryCount) => timer(retryCount * 1000),
      }),
      catchError((err: any) => {
        console.error('[TCG] ALM extraction failed:', err);
        this.isStartingExtract = false;
        this.extractStartError = err?.error?.message || err?.message || 'Extraction failed. Please try again.';
        return EMPTY;
      })
    ).subscribe({
      next: (response: any) => {
        this.configId = response?.data?.configId;
        this.closeForParentPolling();
      },
    });
  }

  /** Close modal immediately so the parent can poll and show the counts bar + list. */
  private closeForParentPolling(): void {
    this.isStartingExtract = false;
    const fromTimestamp = this.almExtractFrom
      ? new Date(this.almExtractFrom).toISOString()
      : undefined;
    this.activeModal.close({
      ...this._pendingResult,
      extractConfigId: this.configId,
      pollExtraction: true,
      extractedStories: [],
      fromTimestamp,
    });
  }

  /**
   * Full BPD pipeline for "From other documents":
   *   1. POST tcg/file/bpd/upload
   *   2. POST tcg/file/bpd/knowledgegraph
   *   3. GET  tcg/file/bpd/knowledgegraph/... — poll every 30 s until complete
   *   4. POST tcg/file/bpd/testcases
   *   5. GET  tcg/file/bpd/testcases/... — poll every 30 s until complete
   */
  private startBpdPipeline(connectionId: string): void {
    this.currentStep = 4;
    this.progressValue = 0;
    this.progressPhase = 'uploading';
    this.progressText = 'Uploading document...';
    this.isProgressError = false;
    this.resetProcessLogs('Uploading document and preparing Knowledge Graph generation...');

    this._pollSub?.unsubscribe();
    this.stopProgressTick();

    const testCaseType = this.getTestCaseType();

    this._pollSub = this.restService
      .uploadBpdDocument(this.projectId, connectionId)
      .pipe(
        // ── Phase 2: trigger KG generation ────────────────────────────────
        tap(() => {
          this.progressValue = 20;
          this.progressPhase = 'kg';
          this.progressText = 'Generating Knowledge Graph...';
          this.completeProcessLogStep(
            'Document upload completed successfully.',
            'Generating Knowledge Graph from the uploaded document...'
          );
        }),
        concatMap(() =>
          this.restService.generateBpdKnowledgeGraph(this.projectId, connectionId),
        ),
        tap(() => {
          this.progressValue = 35;
          this.progressText = 'Building Knowledge Graph...';
          this.updateLiveThinking('Building Knowledge Graph...');
          // Slowly creep from 35 → 64 while waiting for KG polls
          this.startProgressTick(35, 64);
        }),
        // ── Phase 3: poll KG progress every 30 s ──────────────────────────
        concatMap(() =>
          timer(0, 30000).pipe(
            switchMap(() =>
              this.restService.getBpdKgProgress(this.projectId, connectionId),
            ),
            tap((pollRes: any) => {
              // If the API reports a real percentage, honour it; otherwise the ticker handles it
              const raw: number =
                pollRes?.data?.progress ?? pollRes?.data?.percentage ??
                pollRes?.progress ?? pollRes?.percentage ?? 0;
              if (raw > 0) {
                this.progressValue = Math.min(35 + Math.round(raw * 0.3), 64);
              }
              this.progressText =
                pollRes?.data?.message ?? pollRes?.message ?? 'Building Knowledge Graph...';
              this.updateLiveThinking(this.progressText);
            }),
            filter((pollRes: any) => {
              const s = (pollRes?.data?.status ?? pollRes?.status ?? '').toUpperCase();
              return s === 'COMPLETED' || s === 'DONE' || s === 'SUCCESS';
            }),
            first(),
            takeUntil(this._destroy$),
          ),
        ),
        // ── Phase 4: generate test cases ──────────────────────────────────
        tap(() => {
          this.stopProgressTick();
          this.progressValue = 65;
          this.progressPhase = 'generating';
          this.progressText = 'Generating test cases...';
          this.completeProcessLogStep(
            'Knowledge Graph generation completed successfully.',
            'Generating test cases from the Knowledge Graph...'
          );
        }),
        concatMap(() =>
          this.restService.generateBpdTestCases(this.projectId, connectionId, testCaseType),
        ),
        tap(() => {
          this.progressValue = 80;
          this.progressText = 'Fetching test cases...';
          this.updateLiveThinking('Fetching generated test cases...');
          // Creep very slowly from 80 → 98 (1 % every 6 s ≈ 108 s to ceiling)
          this.startProgressTick(80, 98, 6000);
        }),
        // ── Phase 5: poll GET testcases until status = "COMPLETED" ────────
        concatMap(() =>
          timer(0, 30000).pipe(
            switchMap(() =>
              this.restService.getBpdTestCases(this.projectId, connectionId),
            ),
            tap((pollRes: any) => {
              this.progressText = pollRes?.data?.message ?? pollRes?.message ?? 'Fetching test cases...';
              this.updateLiveThinking(this.progressText);
            }),
            filter((pollRes: any) => {
              const s = (pollRes?.data?.status ?? pollRes?.status ?? '').toUpperCase();
              return s === 'COMPLETED' || s === 'DONE' || s === 'SUCCESS';
            }),
            first(),
            takeUntil(this._destroy$),
          ),
        ),
      )
      .subscribe({
        next: (res: any) => {
          this.stopProgressTick();
          this.progressValue = 100;
          this.progressPhase = 'done';
          this.progressText = 'Generation complete!';
          this.completeProcessLogStep(
            'Test case generation completed successfully.',
            undefined,
            true
          );
          this._pollSub?.unsubscribe();
          setTimeout(() => this.activeModal.close({
            ...this._pendingResult,
            connectionId: this._pendingResult?.connectionId || connectionId,
            testCases: res,
          }), 600);
        },
        error: (err: any) => {
          this.stopProgressTick();
          console.error('[TCG] BPD pipeline error', err);
          this.isProgressError = true;
          this.progressText =
            err?.error?.message ||
            err?.message ||
            'An error occurred. Please try again.';
          this.failProcessLog(this.progressText);
        },
      });
  }

  /**
   * Slowly increments progressValue by 1 every `intervalMs` milliseconds
   * until it reaches `ceiling`. Default interval is 3 s (KG phase).
   * Pass a larger interval for phases that should move even slower.
   */
  private startProgressTick(floor: number, ceiling: number, intervalMs = 3000): void {
    this.stopProgressTick();
    this._tickTimer = setInterval(() => {
      if (this.progressValue < ceiling) {
        this.progressValue = Math.min(this.progressValue + 1, ceiling);
      }
    }, intervalMs);
  }

  private stopProgressTick(): void {
    if (this._tickTimer !== null) {
      clearInterval(this._tickTimer);
      this._tickTimer = null;
    }
  }

  /** Maps the user's test-type selections to the backend enum value. */
  private getTestCaseType(): string {
    if (this.nonFunctionalTestCases) return 'non_functional';
    switch (this.testType) {
      case 'sanity':     return 'functional_sanity';
      case 'regression': return 'functional_regression';
      default:           return 'functional_smoke';
    }
  }

  private startProgress(): void {
    this.currentStep = 4;
    this.progressValue = 0;
    this.progressPhase = 'generating';
    this.progressText = 'Generating test cases...';
    this.isProgressError = false;
    this.resetProcessLogs('Generating test cases from the selected document...');

    const totalDuration = 5000;
    const tickInterval = 100;
    const totalTicks = totalDuration / tickInterval;
    let tick = 0;

    this._progressTimer = setInterval(() => {
      tick++;
      this.progressValue = Math.min(Math.round((tick / totalTicks) * 100), 100);
      this.updateLiveThinking(this.progressText);

      if (tick >= totalTicks) {
        clearInterval(this._progressTimer);
        this._progressTimer = null;
        this.progressPhase = 'done';
        this.progressText = 'Generation complete!';
        this.completeProcessLogStep('Test case generation completed successfully.', undefined, true);
        setTimeout(() => this.activeModal.close(this._pendingResult), 400);
      }
    }, tickInterval);
  }

  /** Seed timeline with a live thinking row. */
  private resetProcessLogs(thinking: string): void {
    this.processLogs = [{ step: 1, thinking, evaluation: '' }];
  }

  /** Update the live (latest) thinking text without completing the step. */
  private updateLiveThinking(thinking: string): void {
    if (!this.processLogs.length) {
      this.resetProcessLogs(thinking);
      return;
    }
    const latest = this.processLogs[this.processLogs.length - 1];
    this.processLogs = [
      ...this.processLogs.slice(0, -1),
      { ...latest, thinking },
    ];
  }

  /**
   * Mark the current live step as completed (evaluation), optionally start a new live step.
   * When `finalize` is true, only completes — no new live row.
   */
  private completeProcessLogStep(
    evaluation: string,
    nextThinking?: string,
    finalize = false
  ): void {
    const completed: ProcessLogEntry[] = this.processLogs.map((log, idx) => {
      if (idx !== this.processLogs.length - 1) return log;
      return { ...log, evaluation: evaluation || log.evaluation || log.thinking || '', thinking: '' };
    });

    if (finalize || !nextThinking) {
      this.processLogs = completed;
      return;
    }

    this.processLogs = [
      ...completed,
      { step: completed.length + 1, thinking: nextThinking, evaluation: '' },
    ];
  }

  private failProcessLog(message: string): void {
    if (!this.processLogs.length) {
      this.processLogs = [{ step: 1, thinking: message, evaluation: '' }];
      return;
    }
    const latest = this.processLogs[this.processLogs.length - 1];
    this.processLogs = [
      ...this.processLogs.slice(0, -1),
      { ...latest, thinking: message },
    ];
  }
}
